import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Image,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MdRefresh,
  MdRoute,
  MdScale,
  MdExpandLess,
  MdExpandMore,
} from 'react-icons/md';
import { FaRoute } from 'react-icons/fa';
import { PageShell } from 'components/layout/PageShell';
import StatusBadge from '../components/StatusBadge';
import {
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedPageToolbar,
  UnifiedResultsSection,
  UnifiedSurfaceCard,
  useUnifiedButtonStyles,
  useUnifiedInteractiveCardStyles,
  useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTabsStyles } from 'components/layout/useUnifiedUiStyles';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import { getMyQuotes } from 'domains/listing/services/listingService';
import { useTranslation } from 'i18n/TranslationProvider';
import { mapTemplateTitleToTranslation } from 'domains/shared/utils/categoryUtils';
import { CATEGORIES } from 'domains/shared/utils/categoryUtils';

// Accepted first — most actionable. Active listings within that tab sorted to top.
const TABS = [
  { label: 'accepted', status: 'accepted', key: 'Accepted' },
  { label: 'pending', status: 'pending', key: 'Pending' },
  { label: 'rejected', status: 'rejected', key: 'Rejected' },
  { label: 'expired', status: 'expired', key: 'Expired' },
];

const ACTIVE_LISTING_STATUSES = new Set([
  'accepted',
  'awaiting_payment',
  'paid',
  'planned',
  'in_transit',
  'pickedUp',
  'delivered',
]);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateRange(from, to, t) {
  if (!from && !to) return t('Flexible dates');
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '?';
  if (from && to && from !== to) return `${fmt(from)} – ${fmt(to)}`;
  return fmt(from || to);
}

function formatCategoryLabel(value) {
  return String(value || 'other').replace(/_/g, ' ');
}

function formatDistance(distanceKm, t) {
  if (!distanceKm) return null;
  const km = Number(distanceKm);
  if (isNaN(km)) return null;
  if (km < 10) return `${km.toFixed(1)} ${t('km')}`;
  return `${Math.round(km)} ${t('km')}`;
}

function formatMoney(value, t) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? `${t('EUR')} ${numeric.toFixed(2)}`
    : null;
}

function getItemDetails(listing, t) {
  const items = Array.isArray(listing.items)
    ? listing.items.filter(Boolean)
    : [];
  if (!items.length)
    return { title: t('No item details'), facts: [], dimensions: null };

  const first = items[0] || {};
  const dimensionSource =
    items.find((e) => e?.weight || e?.length || e?.width || e?.height) || first;
  const title =
    [first.make, first.model].filter(Boolean).join(' ').trim() ||
    first.itemDescription ||
    first.petType ||
    t('Shipment details');

  const facts = [];
  if (items.length)
    facts.push(
      `${items.length} ${items.length === 1 ? t('item') : t('items')}`,
    );
  if (first.weight) facts.push(`${first.weight} kg`);

  const dimensions = {
    weight: dimensionSource.weight,
    length: dimensionSource.length,
    width: dimensionSource.width,
    height: dimensionSource.height,
    itemCount: items.length,
  };

  return { title, facts: facts.slice(0, 4), dimensions };
}

function getMapThumbnailUrl(listing) {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const origin = listing.fromCoords || listing.publicFromCoords;
  const destination = listing.toCoords || listing.publicToCoords;
  if (!apiKey || (!origin && !destination)) return null;

  const parts = ['size=240x180', 'scale=2', 'maptype=roadmap', `key=${apiKey}`];
  if (origin)
    parts.push(`markers=color:0x4285F4|label:A|${origin.lat},${origin.lng}`);
  if (destination)
    parts.push(
      `markers=color:0xEA4335|label:B|${destination.lat},${destination.lng}`,
    );

  if (origin && destination) {
    const latDiff = Math.abs(origin.lat - destination.lat);
    const lngDiff = Math.abs(origin.lng - destination.lng);
    const MIN_SPAN = 0.14;
    const effectiveLat = Math.max(latDiff, MIN_SPAN);
    const effectiveLng = Math.max(lngDiff, MIN_SPAN);
    const PAD = 0.45;
    const minLat = Math.min(origin.lat, destination.lat) - effectiveLat * PAD;
    const maxLat = Math.max(origin.lat, destination.lat) + effectiveLat * PAD;
    const minLng = Math.min(origin.lng, destination.lng) - effectiveLng * PAD;
    const maxLng = Math.max(origin.lng, destination.lng) + effectiveLng * PAD;
    parts.push(`visible=${minLat},${minLng}|${maxLat},${maxLng}`);
  } else {
    const point = origin || destination;
    parts.push(`center=${point.lat},${point.lng}`);
    parts.push('zoom=9');
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${parts.join('&')}`;
}

function QuoteCard({ quote }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
  const { primaryButtonProps, secondaryButtonProps } = useUnifiedButtonStyles();
  const { transition, hoverProps } = useUnifiedInteractiveCardStyles();

  const cardBg = useColorModeValue(
    'rgba(255,255,255,0.92)',
    'rgba(15, 23, 42, 0.9)',
  );
  const titleColor = useColorModeValue('navy.800', 'white');
  const accentColor = useColorModeValue('brand.500', 'brand.200');
  const categoryBadgeBg = useColorModeValue('#6B46F6', '#8B5CF6');
  const timelineLineColor = useColorModeValue('#DDD6FE', '#6D28D9');
  const neutralChipBg = useColorModeValue('#F8FAFC', 'whiteAlpha.120');
  const quotePanelBg = useColorModeValue('#EEF4FF', 'rgba(49, 130, 206, 0.16)');
  const metaBadgeBg = useColorModeValue('#E8EEFF', 'rgba(72, 187, 255, 0.18)');
  const shadow = useColorModeValue(
    '0 22px 50px rgba(15, 23, 42, 0.08)',
    '0 18px 40px rgba(2, 6, 23, 0.35)',
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const { listing = {} } = quote;
  const status = (quote.status || quote._status || '').toLowerCase();
  const isFixedPriceAcceptance = quote.source === 'fixed_price_accept';
  const netPrice = quote.netPrice ?? quote._netPrice;
  const vatRate = quote.vatRate ?? quote._vatRate;
  const listingId = listing._id || listing.id;

    const category = CATEGORIES.find((o) => o.id === listing.category);
    const fromLabel =
        [listing.fromCity, listing.fromPostcode, listing.fromCountry].filter(Boolean).join(', ') ||
        t('Pick-up pending');
    const toLabel =
        [listing.toCity, listing.toPostcode, listing.toCountry].filter(Boolean).join(', ') ||
        t('Delivery pending');
    const pickupLabel = formatDateRange(
        quote.proposedPickupDate || listing.pickupDateFrom,
        !quote.proposedPickupDate ? listing.pickupDateTo : null,
        t,
    );
    const deliveryLabel = formatDateRange(
        quote.proposedDeliveryDate || listing.deliveryDateFrom,
        !quote.proposedDeliveryDate ? listing.deliveryDateTo : null,
        t,
    );

  const grossPrice =
    netPrice != null && vatRate != null
      ? (netPrice * (1 + vatRate / 100)).toFixed(2)
      : netPrice != null
        ? netPrice.toFixed(2)
        : '—';

  const clientTypeLabel =
    listing.clientType === 'company' ? t('Company') : t('Private Person');
  const item = getItemDetails(listing, t);
  const distanceText = formatDistance(listing.distanceKm, t);
  const mapUrl = getMapThumbnailUrl(listing);
  const postedDate = quote.createdAt ? formatDate(quote.createdAt) : '—';
  const isManageable =
    status === 'accepted' && ACTIVE_LISTING_STATUSES.has(listing.status);
  const clientPriceText = formatMoney(listing.clientPriceGross, t);
  const weightText = item.dimensions?.weight
    ? `${item.dimensions.weight} kg`
    : null;
  const itemCount = item.dimensions?.itemCount || 0;
  const itemCountText = `${itemCount} ${itemCount === 1 ? t('item') : t('items')}`;

  return (
    <UnifiedSurfaceCard
      bg={cardBg}
      p={{ base: 4, md: 5, xl: 6 }}
      borderRadius={{ base: '26px', xl: '34px' }}
      position="relative"
      transition={transition}
      boxShadow={shadow}
      border="1px solid"
      borderColor={subtleBorder}
      _hover={hoverProps}
    >
      <HStack
        position="absolute"
        top={{ base: 4, md: 5, xl: 6 }}
        right={{ base: 4, md: 5, xl: 6 }}
        align="center"
        spacing={3}
        zIndex={1}
        flexWrap="wrap"
        justify="flex-end"
      >
        {listing.humanId && (
          <Text
            fontSize="10px"
            color={mutedText}
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            #{listing.humanId}
          </Text>
        )}
        <Text
          fontSize="10px"
          color={mutedText}
          fontWeight="800"
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          {t('Submitted {{date}}', { date: postedDate })}
        </Text>
        <Badge
          bg={categoryBadgeBg}
          color="white"
          borderRadius="full"
          px={3.5}
          py={1}
          fontSize="xs"
          fontWeight="700"
          textTransform="none"
        >
          {category?.label
            ? t(category.label)
            : t(formatCategoryLabel(listing.category))}
        </Badge>
        {isFixedPriceAcceptance ? (
          <Badge
            colorScheme="purple"
            borderRadius="full"
            px={3.5}
            py={1}
            fontSize="xs"
            fontWeight="700"
            textTransform="none"
          >
            {t('Fixed price')}
          </Badge>
        ) : null}
        <Box>
          <StatusBadge
            status={status}
            borderRadius="full"
            px={3.5}
            py={1}
            fontSize="10px"
            fontWeight="800"
            textTransform="uppercase"
            bg={metaBadgeBg}
            translation={t}
          />
        </Box>
      </HStack>

      <Grid
        alignItems="center"
        templateColumns={{
          base: '1fr',
          xl: '220px minmax(260px, 320px) minmax(0, 1fr) 220px',
        }}
      >
        <Box
          w={{ base: '100%', xl: '220px' }}
          h={{ base: '160px', xl: '170px' }}
          borderRadius="22px"
          overflow="hidden"
          bg={subtleBg}
          border="1px solid"
          borderColor={subtleBorder}
          flexShrink={0}
          position="relative"
          boxShadow="0 14px 28px rgba(15, 23, 42, 0.08)"
        >
          {mapUrl ? (
            <Image
              src={mapUrl}
              alt={t('Route map')}
              w="100%"
              h="100%"
              objectFit="cover"
              fallback={
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Icon
                    as={FaRoute}
                    boxSize={8}
                    color={accentColor}
                    opacity={0.5}
                  />
                </Flex>
              }
            />
          ) : (
            <Flex
              w="100%"
              h="100%"
              align="center"
              justify="center"
              direction="column"
              gap={2}
              px={4}
            >
              <Icon
                as={FaRoute}
                boxSize={8}
                color={accentColor}
                opacity={0.5}
              />
              <Text
                fontSize="sm"
                color={mutedText}
                textAlign="center"
                fontWeight="700"
              >
                {fromLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                textAlign="center"
                fontWeight="600"
              >
                {toLabel}
              </Text>
            </Flex>
          )}
        </Box>
        <HStack
          align="stretch"
          spacing={{ base: 3, md: 4 }}
          minW={0}
          flex={1}
          pl={5}
        >
          <VStack spacing={0} minW={{ base: '44px', md: '54px' }} pt={1}>
            <Flex
              w={{ base: '38px', md: '44px' }}
              h={{ base: '38px', md: '44px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#4285F4"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 8px 18px rgba(66, 133, 244, 0.25)"
            >
              A
            </Flex>
            <Box
              w="2px"
              flex="1"
              minH={{ base: '52px', md: '60px' }}
              bg={timelineLineColor}
              borderRadius="full"
              my={2}
              mx="auto"
            />
            <Flex
              w={{ base: '36px', md: '42px' }}
              h={{ base: '36px', md: '42px' }}
              align="center"
              justify="center"
              borderRadius="full"
              bg="#EA4335"
              color="white"
              fontWeight="800"
              fontSize={{ base: 'md', md: 'lg' }}
              boxShadow="0 14px 24px rgba(234, 67, 53, 0.26)"
            >
              B
            </Flex>
          </VStack>

          <VStack
            align="stretch"
            spacing={{ base: 6, md: 7 }}
            minW={0}
            justify="center"
          >
            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Pick-up')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {fromLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="600"
                noOfLines={1}
              >
                {pickupLabel}
              </Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {t('Delivery')}
              </Text>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="800"
                color={titleColor}
                lineHeight="1.3"
                noOfLines={1}
              >
                {toLabel}
              </Text>
              <Text
                fontSize="xs"
                color={mutedText}
                fontWeight="600"
                noOfLines={1}
              >
                {deliveryLabel}
              </Text>
            </VStack>
          </VStack>
        </HStack>
        <VStack
          align="stretch"
          spacing={{ base: 4, md: 5 }}
          minW={0}
          justify="center"
          flex={1}
        >
          <HStack spacing={3} align="center" flexWrap="wrap">
            <Text
              pt={2}
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight="700"
              color={titleColor}
              lineHeight="1.25"
              noOfLines={2}
              width={'100%'}
            >
              {mapTemplateTitleToTranslation(listing.title || item.title, t)}
            </Text>
          </HStack>

          <HStack spacing={3} flexWrap="wrap">
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {clientTypeLabel}
              </Text>
            </Box>
            <Box
              px={3.5}
              py={2}
              borderRadius="18px"
              bg={neutralChipBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="700"
                color={titleColor}
              >
                {itemCountText}
              </Text>
            </Box>
            {clientPriceText ? (
              <Box
                px={3.5}
                py={2}
                borderRadius="18px"
                bg={neutralChipBg}
                border="1px solid"
                borderColor={subtleBorder}
              >
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  color={titleColor}
                >
                  {clientPriceText}
                </Text>
              </Box>
            ) : null}
            {quote.vehicleLabel && (
              <Box
                px={3.5}
                py={2}
                borderRadius="18px"
                bg={neutralChipBg}
                border="1px solid"
                borderColor={subtleBorder}
              >
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="700"
                  color={titleColor}
                >
                  {quote.vehicleLabel}
                </Text>
              </Box>
            )}
          </HStack>

          <HStack spacing={{ base: 4, md: 6 }} flexWrap="wrap" align="center">
            {weightText ? (
              <HStack spacing={1.5}>
                <Icon as={MdScale} boxSize={3.5} color={accentColor} />
                <Text
                  fontSize="sm"
                  color={titleColor}
                  fontWeight="700"
                >
                  {weightText}
                </Text>
              </HStack>
            ) : null}
            {distanceText ? (
              <HStack spacing={1.5}>
                <Icon as={MdRoute} boxSize={3.5} color={accentColor} />
                <Text
                  fontSize="sm"
                  color={titleColor}
                  fontWeight="700"
                >
                  {distanceText}
                </Text>
              </HStack>
            ) : null}
          </HStack>
        </VStack>
        <VStack
          align={{ base: 'stretch', lg: 'flex-end' }}
          justify="center"
          spacing={3}
          minW={{ lg: '220px' }}
          pt={{ lg: 8 }}
        >
          <Flex
            px={4}
            py={3.5}
            borderRadius="22px"
            bg={quotePanelBg}
            border="1px solid"
            borderColor={subtleBorder}
            align="flex-start"
            justify="space-between"
            gap={4}
            w="100%"
          >
            <Box>
              <Text
                fontSize="10px"
                fontWeight="800"
                color={mutedText}
                textTransform="uppercase"
                letterSpacing="0.06em"
              >
                {t('Your quote')}
              </Text>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                fontWeight="900"
                color="brand.500"
                _dark={{ color: 'brand.200' }}
                lineHeight="1.1"
              >
                €{grossPrice}
              </Text>
              {quote.quoteExpiresAt && (
                <Badge
                  mt={2}
                  bg={neutralChipBg}
                  color={titleColor}
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="10px"
                  fontWeight="700"
                  textTransform="none"
                >
                  {t('Expires {{date}}', {
                    date: formatDate(quote.quoteExpiresAt),
                  })}
                </Badge>
              )}
            </Box>
          </Flex>

          {listingId && (
            <Button
              {...primaryButtonProps}
              fontSize="sm"
              w="100%"
              onClick={() => navigate(`/listings/${listingId}`)}
            >
              {isManageable ? t('Manage quote') : t('View listing')}
            </Button>
          )}

          <Button
            {...secondaryButtonProps}
            fontSize="sm"
            w="100%"
            rightIcon={
              <Icon as={isExpanded ? MdExpandLess : MdExpandMore} boxSize={4} />
            }
            onClick={() => setIsExpanded((v) => !v)}
          >
            {t('Quick view')}
          </Button>
        </VStack>
      </Grid>

      <Collapse in={isExpanded} animateOpacity>
        <Box mt={3}>
          <ListingItemsSection
            items={listing.items}
            category={listing.category}
          />
        </Box>
      </Collapse>
    </UnifiedSurfaceCard>
  );
}

function QuoteList({ status, refreshKey }) {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyQuotes({ status });
      let result = Array.isArray(data) ? data : (data?.quotes ?? []);
      // For accepted tab: active listings (in-flight transport) float to the top
      if (status === 'accepted') {
        result = [...result].sort((a, b) => {
          const aActive = ACTIVE_LISTING_STATUSES.has(a.listing?.status);
          const bActive = ACTIVE_LISTING_STATUSES.has(b.listing?.status);
          if (aActive && !bActive) return -1;
          if (!aActive && bActive) return 1;
          return 0;
        });
      }
      setQuotes(result);
    } catch {
      setError(t('Failed to load quotes.'));
    } finally {
      setLoading(false);
    }
  }, [status, t]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return <UnifiedLoadingState label="Loading quotes..." minH="180px" />;
  }

  if (error) {
    return (
      <Text color="red.400" fontSize="sm">
        {error}
      </Text>
    );
  }

  if (quotes.length === 0) {
    return (
      <UnifiedEmptyState
        title={t('No {{status}} quotes', { status: t(status) })}
        description={
          status === 'pending'
            ? t('Submit quotes on open listings to see them here.')
            : t('You have no {{status}} quotes at the moment.', {
                status: t(status),
              })
        }
      />
    );
  }

  return (
    <VStack align="stretch" spacing={3} mt={4}>
      {quotes.map((q, idx) => (
        <QuoteCard key={q._id || q.id || idx} quote={q} />
      ))}
    </VStack>
  );
}

export default function MyQuotes() {
  const { t } = useTranslation();

  const { tabListProps, tabProps } = useUnifiedTabsStyles();
  const { ghostButtonProps } = useUnifiedButtonStyles();
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const activeTab = TABS[tabIndex] || TABS[0];
  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <UnifiedPageToolbar>
          <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
            <VStack align="start" spacing={1}>
              <Text fontWeight="800" fontSize={{ base: 'lg', md: 'xl' }}>
                {t('quoteWorkspace.title')}
              </Text>
              <Text fontSize="sm" color={mutedText}>
                {t('quoteWorkspace.description')}
              </Text>
            </VStack>
            <IconButton
              icon={<Icon as={MdRefresh} />}
              aria-label="Refresh"
              {...ghostButtonProps}
              onClick={() => setRefreshKey((k) => k + 1)}
            />
          </Flex>

          <Tabs variant="unstyled" index={tabIndex} onChange={setTabIndex}>
            <TabList {...tabListProps}>
              {TABS.map((tab) => (
                <Tab key={tab.status} {...tabProps} fontSize="sm">
                  {`${t(`${tab.key}.plural`)}`}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </UnifiedPageToolbar>

        <UnifiedResultsSection
          title={`${t(`${activeTab.key}.plural`)} ${t('quotes')}`}
          meta={
            activeTab.status === 'accepted'
              ? t('quotes.accepted.description')
              : `Review your ${activeTab.label.toLowerCase()} quote activity with the same delivery-card layout used in marketplace search.`
          }
        >
          <QuoteList status={activeTab.status} refreshKey={refreshKey} />
        </UnifiedResultsSection>
      </VStack>
    </PageShell>
  );
}
